// "Build a website from scratch": the intake ops fills in. A later agent reads a project
// and builds the site; this module only defines and validates the input.

/** Only 'draft' is used today. The rest are planned for the build agent. */
export type ScratchStatus = 'draft' | 'queued' | 'building' | 'ready' | 'failed'

export const SCRATCH_LIMITS = {
  businessName: 120,
  text: 4000,
  inspiration: 3,
  colors: 5,
  images: 20,
  logoBytes: 2 * 1024 * 1024,
  imageBytes: 10 * 1024 * 1024,
} as const

export type ScratchImageType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif' | 'image/svg+xml'

/** File extension per accepted type. SVG is accepted for the logo only. */
export const SCRATCH_IMAGE_EXT: Record<ScratchImageType, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
}
export const SCRATCH_RASTER_TYPES: ScratchImageType[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
export const SCRATCH_LOGO_TYPES: ScratchImageType[] = [...SCRATCH_RASTER_TYPES, 'image/svg+xml']

/**
 * What the site is for. It steers the structure the builder proposes (pages, calls to action,
 * features); the description and instructions can complement or override it.
 */
export const SCRATCH_SITE_TYPES = {
  information: { label: 'Information', hint: 'Presents the business: who, what, where, how to reach it.' },
  commercial: { label: 'Commercial', hint: 'Sells services or products: offers, prices, quotes, bookings.' },
  blog: { label: 'Blog', hint: 'Publishes articles or news regularly.' },
  portfolio: { label: 'Portfolio', hint: 'Shows work: projects, photos, case studies.' },
} as const
export type ScratchSiteType = keyof typeof SCRATCH_SITE_TYPES

export interface ScratchInput {
  /** Bare lowercase domain, e.g. example.com. */
  domain: string
  businessName: string
  siteType: ScratchSiteType
  /** 1-3 https URLs of reference sites, for layout and style cues only. */
  inspirationUrls: string[]
  /** 1-5 lowercase hex colors (#rgb or #rrggbb). */
  colors: string[]
  description: string
  /** Free-hand notes for the builder; '' when none. */
  instructions: string
}

export interface ScratchFile {
  key: string
  name: string
  size: number
  type: ScratchImageType
}

export interface ScratchProject extends ScratchInput {
  id: string
  status: ScratchStatus
  /** R2 key of the logo, or null. */
  logoKey: string | null
  images: ScratchFile[]
  createdBy: string
  createdAt: number
  updatedAt: number
}

export type ScratchErrors = Partial<Record<keyof ScratchInput, string>>

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/
const LABEL_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)$/
const TLD_RE = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/

function parseHost(input: string): URL | null {
  const s = input.trim()
  if (!s) return null
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`)
  } catch {
    return null
  }
}

/** True for a public DNS name with at least two labels. Refuses IP literals, localhost and single labels. */
function isPublicHostname(h: string): boolean {
  if (h.length > 253) return false
  const labels = h.split('.')
  return labels.length >= 2 && labels.every((l) => LABEL_RE.test(l)) && TLD_RE.test(labels.at(-1)!)
}

/**
 * Turns a pasted domain or URL ("https://www.Example.com/about") into a bare domain ("example.com").
 * Returns null when it is not a public domain name. Unicode names come back in punycode.
 */
export function normaliseDomain(input: string): string | null {
  const u = parseHost(input)
  if (!u || u.username || u.password || u.port) return null
  const h = u.hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '')
  return isPublicHostname(h) ? h : null
}

/** An https URL on a public hostname, or null. A bare host gets https:// prepended. */
export function normaliseInspirationUrl(input: string): string | null {
  const u = parseHost(input)
  if (!u || u.protocol !== 'https:' || u.username || u.password || input.length > 2000) return null
  if (!isPublicHostname(u.hostname.toLowerCase().replace(/\.$/, ''))) return null
  u.hash = ''
  return u.toString()
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

function list(v: unknown): string[] {
  return Array.isArray(v) ? v.map(text).filter(Boolean) : []
}

/**
 * Validates and normalises the intake fields. Pure: the web form and the API both call it,
 * and the API trusts only its own call. Empty list entries are dropped and duplicates removed.
 */
export function validateScratch(raw: unknown): { ok: true; value: ScratchInput } | { ok: false; errors: ScratchErrors } {
  const b = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const errors: ScratchErrors = {}

  const domain = normaliseDomain(text(b.domain))
  if (!text(b.domain)) errors.domain = 'Enter the domain.'
  else if (!domain) errors.domain = 'Enter a domain such as example.com.'

  const businessName = text(b.businessName)
  if (!businessName) errors.businessName = 'Enter the business name.'
  else if (businessName.length > SCRATCH_LIMITS.businessName) errors.businessName = `Keep the business name under ${SCRATCH_LIMITS.businessName} characters.`

  const siteType = text(b.siteType) as ScratchSiteType
  if (!Object.hasOwn(SCRATCH_SITE_TYPES, siteType)) errors.siteType = 'Choose the type of site.'

  const urls = list(b.inspirationUrls)
  const badUrl = urls.find((u) => !normaliseInspirationUrl(u))
  const inspirationUrls = [...new Set(urls.map(normaliseInspirationUrl).filter((u) => u !== null))]
  if (badUrl) errors.inspirationUrls = `Not a valid https URL: ${badUrl.slice(0, 100)}`
  else if (inspirationUrls.length < 1) errors.inspirationUrls = 'Add at least one inspiration website.'
  else if (inspirationUrls.length > SCRATCH_LIMITS.inspiration) errors.inspirationUrls = `Add at most ${SCRATCH_LIMITS.inspiration} inspiration websites.`

  const rawColors = list(b.colors).map((c) => c.toLowerCase())
  const colors = [...new Set(rawColors)]
  const badColor = rawColors.find((c) => !HEX_RE.test(c))
  if (badColor) errors.colors = `Not a hex color: ${badColor.slice(0, 20)}. Use #rgb or #rrggbb.`
  else if (colors.length < 1) errors.colors = 'Add at least one color.'
  else if (colors.length > SCRATCH_LIMITS.colors) errors.colors = `Add at most ${SCRATCH_LIMITS.colors} colors.`

  const description = text(b.description)
  if (!description) errors.description = 'Describe what the business does.'
  else if (description.length > SCRATCH_LIMITS.text) errors.description = `Keep the description under ${SCRATCH_LIMITS.text} characters.`

  const instructions = text(b.instructions)
  if (instructions.length > SCRATCH_LIMITS.text) errors.instructions = `Keep the instructions under ${SCRATCH_LIMITS.text} characters.`

  if (Object.keys(errors).length) return { ok: false, errors }
  return { ok: true, value: { domain: domain!, businessName, siteType, inspirationUrls, colors, description, instructions } }
}

function startsWith(b: Uint8Array, sig: number[], at = 0): boolean {
  return b.length >= at + sig.length && sig.every((x, i) => b[at + i] === x)
}

const ascii = (s: string) => [...s].map((ch) => ch.charCodeAt(0))

// XML prolog, comments and doctype may precede the <svg> root.
const SVG_RE = /^﻿?\s*(?:<\?xml[^>]*\?>\s*)?(?:<!--[\s\S]*?-->\s*)*(?:<!doctype\s+svg[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<svg[\s>]/i
// Script vectors that would run if a site later inlines the SVG. Not a sanitiser: the build
// agent must still sanitise or rasterise SVG before it reaches a page.
const SVG_ACTIVE_RE = /<script|<foreignobject|\son[a-z]+\s*=|javascript:/i

/**
 * Detects the image type from the file's bytes; the declared type and file name are not trusted.
 * Returns null for anything else, including an SVG with script content.
 */
export function sniffImage(b: Uint8Array): ScratchImageType | null {
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (startsWith(b, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (startsWith(b, ascii('GIF87a')) || startsWith(b, ascii('GIF89a'))) return 'image/gif'
  if (startsWith(b, ascii('RIFF')) && startsWith(b, ascii('WEBP'), 8)) return 'image/webp'
  let s: string
  try {
    s = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(b)
  } catch {
    return null
  }
  return SVG_RE.test(s.slice(0, 4096)) && !SVG_ACTIVE_RE.test(s) ? 'image/svg+xml' : null
}

/**
 * File name safe for an R2 key and a content-disposition header: [A-Za-z0-9_-] and at most 60 chars,
 * then `.ext`. The extension comes from the sniffed type, never from the uploaded name.
 */
export function safeFileName(name: string, ext: string): string {
  const base = (name.split(/[\\/]/).pop() ?? '').replace(/\.[^.]*$/, '')
  const s = base.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
  return `${s || 'image'}.${ext}`
}
